import * as React from 'react'
import styled from 'styled-components'
import { login, signup } from 'src/api/operations/auth'
import { navigate } from 'gatsby'
import { Button } from 'src/components/Button'
import {
  SimplePropertyField,
  StringInput
} from 'src/components/PropertyField/SimplePropertyField'

function useLoading() {
  const [isLoading, setState] = React.useState(false)
  const mount = React.useRef(false)
  React.useEffect(() => {
    mount.current = true
    return () => void (mount.current = false)
  }, [])
  function load<A>(aPromise: Promise<A>) {
    setState(true)
    return aPromise.finally(() => mount.current && setState(false))
  }
  return [isLoading, load] as [boolean, <A>(aPromise: Promise<A>) => Promise<A>]
}

const InputWrapper = styled.div`
  grid-column: 2 / 3;
  outline: none;
  height: 32px;
  margin: 0 8px 32px 8px;
`

const StyledInput = styled.input`
  background: #f1f1f1;
  padding: 0 8px;
  outline: none;
  border: none;
`

const EmailInput = styled(StyledInput).attrs((props) => ({
  type: 'email',
  name: 'email',
  placeholder: 'email'
}))``

const PasswordInput = styled(StyledInput).attrs((props) => ({
  type: 'password',
  name: 'password',
  placeholder: 'password'
}))``

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 320px 1fr;
`

const FormButton = styled(Button)`
  grid-column: 2 / 3;
  margin-bottom: 32px;
`

const Message = styled.p`
  grid-column: 2 / 3;
  height: 32px;
  margin: 0 8px 32px 8px;
`

export const Auth = ({ context }: { context: 'signup' | 'login' }) => {
  const formRef = React.useRef<HTMLFormElement>(null!)
  const [msg, setMsg] = React.useState('')
  const [isLoading, load] = useLoading()
  return (
    <Form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault()
        const email = formRef.current.email.value
        const password = formRef.current.password.value
        if (email.length === 0 && password.length === 0) {
          setMsg('Please provide an email and a password')
          return
        } else if (email.length === 0) {
          setMsg('Please provide an email')
          return
        } else if (password.length === 0) {
          setMsg('Please provide a password')
          return
        }
        load(
          context === 'login'
            ? login({ email, password })
                .then((user) => {
                  console.log('Success! Logged in', user)
                  navigate('/projects', { replace: true })
                  return
                })
                .catch((err) => setMsg('Error: ' + err.message))
            : signup({ email, password })
                .then((data) => {
                  setMsg(
                    'Success! Please confirm your email address to access Boxtronik!'
                  )
                })
                .catch((err) => setMsg('Error: ' + err.message))
        )
      }}
    >
      <InputWrapper>
        <SimplePropertyField
          {...{
            InputComponent: EmailInput,
            wrapperProps: {
              style: { gridTemplateColumns: '1fr' }
            }
          }}
        />
      </InputWrapper>
      <InputWrapper>
        <SimplePropertyField
          {...{
            InputComponent: PasswordInput,
            wrapperProps: {
              style: { gridTemplateColumns: '1fr' }
            }
          }}
        />
      </InputWrapper>
      <FormButton {...{ disabled: isLoading }}>
        {isLoading
          ? `${context === 'signup' ? '...signing up' : '...login in'}`
          : context}
      </FormButton>
      <Message>{msg}</Message>
    </Form>
  )
}
